import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useAppearanceSettings } from '@/hooks/useAppearanceSettings';
import { Sun, Moon, Monitor, Palette, Eye, Zap, RotateCcw, Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

const EnhancedAppearanceSettings = () => {
  const {
    preferences,
    updatePreference,
    savePreferences,
    resetToDefaults,
    isUpdating,
    hasUnsavedChanges,
  } = useAppearanceSettings();

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun, description: 'Bright and clean interface' },
    { value: 'dark', label: 'Dark', icon: Moon, description: 'Easy on the eyes' },
    { value: 'system', label: 'System', icon: Monitor, description: 'Follows your device' }
  ];

  const accentColors = [
    { value: 'blue', label: 'Blue', className: 'bg-blue-500' },
    { value: 'green', label: 'Green', className: 'bg-green-500' },
    { value: 'purple', label: 'Purple', className: 'bg-purple-500' },
    { value: 'red', label: 'Red', className: 'bg-red-500' },
    { value: 'orange', label: 'Orange', className: 'bg-orange-500' },
    { value: 'pink', label: 'Pink', className: 'bg-pink-500' },
    { value: 'indigo', label: 'Indigo', className: 'bg-indigo-500' },
    { value: 'teal', label: 'Teal', className: 'bg-teal-500' },
    { value: 'yellow', label: 'Yellow', className: 'bg-yellow-500' }
  ];

  return (
    <div className="space-y-6">
      {/* Theme Selection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="bg-gradient-to-br from-background to-muted/30 border-border/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sun className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Theme Selection
                  <Badge variant="secondary" className="text-xs">Live Preview</Badge>
                </CardTitle>
                <CardDescription>Choose your preferred color scheme</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {themeOptions.map((option) => (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => updatePreference('theme', option.value)}
                  className={cn(
                    "relative p-4 rounded-xl border-2 transition-all duration-300 text-left group",
                    preferences.theme === option.value
                      ? "border-primary bg-primary/5 shadow-lg shadow-primary/25"
                      : "border-border/50 bg-card/50 hover:border-primary/50 hover:bg-card/80"
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                      preferences.theme === option.value ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                    )}>
                      <option.icon className="h-4 w-4" />
                    </div>
                    <span className="font-medium">{option.label}</span>
                    {preferences.theme === option.value && (
                      <Badge className="ml-auto text-xs">Active</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{option.description}</p>
                  
                  {preferences.theme === option.value && (
                    <motion.div
                      layoutId="activeTheme"
                      className="absolute inset-0 rounded-xl border-2 border-primary bg-primary/5"
                      initial={false}
                      transition={{ type: "spring", duration: 0.5 }}
                    />
                  )}
                </motion.button>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Accent Color */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="bg-gradient-to-br from-background to-muted/30 border-border/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Palette className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle>Accent Color</CardTitle>
                <CardDescription>Personalize your interface colors</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {accentColors.map((color) => (
                <motion.button
                  key={color.value}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => updatePreference('accent_color', color.value)}
                  className={cn(
                    "relative flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-300",
                    preferences.accent_color === color.value
                      ? "border-primary bg-primary/5 shadow-lg"
                      : "border-border/50 bg-card/50 hover:border-border"
                  )}
                >
                  <div className={cn("w-8 h-8 rounded-full shadow-md", color.className)} />
                  <span className="text-xs font-medium mt-2">{color.label}</span>
                  {preferences.accent_color === color.value && (
                    <div className="absolute inset-0 rounded-xl border-2 border-primary bg-primary/5" />
                  )}
                </motion.button>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Visual Preferences */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card className="bg-gradient-to-br from-background to-muted/30 border-border/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Eye className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle>Visual Preferences</CardTitle>
                <CardDescription>Customize layout and visual effects</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Enable Animations</Label>
                  <p className="text-sm text-muted-foreground">
                    Smooth transitions and micro-interactions
                  </p>
                </div>
                <Switch
                  checked={preferences.animations_enabled}
                  onCheckedChange={(checked) => updatePreference('animations_enabled', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">High Contrast</Label>
                  <p className="text-sm text-muted-foreground">
                    Improve accessibility with enhanced contrast
                  </p>
                </div>
                <Switch
                  checked={preferences.high_contrast}
                  onCheckedChange={(checked) => updatePreference('high_contrast', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Glass Effect</Label>
                  <p className="text-sm text-muted-foreground">
                    Modern glassmorphism design elements
                  </p>
                </div>
                <Switch
                  checked={preferences.glass_effect}
                  onCheckedChange={(checked) => updatePreference('glass_effect', checked)}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base">Layout Density</Label>
              <Select 
                value={preferences.layout_density} 
                onValueChange={(value) => updatePreference('layout_density', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compact">Compact - More content</SelectItem>
                  <SelectItem value="comfortable">Comfortable - Balanced</SelectItem>
                  <SelectItem value="spacious">Spacious - Extra breathing room</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-base">Border Radius</Label>
              <Select 
                value={preferences.border_radius} 
                onValueChange={(value) => updatePreference('border_radius', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sharp - No rounded corners</SelectItem>
                  <SelectItem value="small">Small - Subtle rounding</SelectItem>
                  <SelectItem value="medium">Medium - Balanced rounding</SelectItem>
                  <SelectItem value="large">Large - Very rounded</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-base">Font Size</Label>
              <Select 
                value={preferences.font_size} 
                onValueChange={(value) => updatePreference('font_size', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small - Compact text</SelectItem>
                  <SelectItem value="medium">Medium - Standard size</SelectItem>
                  <SelectItem value="large">Large - Easy to read</SelectItem>
                  <SelectItem value="xlarge">Extra Large - Maximum readability</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card className={cn(
          "transition-all duration-300",
          hasUnsavedChanges 
            ? "bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20" 
            : "bg-gradient-to-br from-background to-muted/30 border-border/50"
        )}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                  hasUnsavedChanges ? "bg-primary/10" : "bg-muted"
                )}>
                  <Zap className={cn(
                    "h-5 w-5 transition-colors",
                    hasUnsavedChanges ? "text-primary" : "text-muted-foreground"
                  )} />
                </div>
                <div>
                  <p className="font-medium">
                    {hasUnsavedChanges ? "You have unsaved changes" : "All changes saved"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {hasUnsavedChanges ? "Save to apply your preferences" : "Your preferences are up to date"}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={resetToDefaults}
                  disabled={isUpdating}
                  className="gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </Button>
                <Button 
                  onClick={savePreferences} 
                  disabled={isUpdating || !hasUnsavedChanges}
                  className={cn(
                    "gap-2 min-w-[120px] transition-all duration-300",
                    hasUnsavedChanges 
                      ? "bg-gradient-to-r from-primary to-secondary hover:shadow-lg" 
                      : ""
                  )}
                >
                  <Save className="w-4 h-4" />
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default EnhancedAppearanceSettings;